import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { runStatsigAutoCapture } from '@statsig/web-analytics';
import { runStatsigSessionReplay } from '@statsig/session-replay';
import { isLocalHost } from '../helpers/environmentHelper';
import Loading from '../components/shared/Loading/Loading';
import AuthContext from './AuthContext';

const FeatureGateContext = createContext<Record<string, never>>({});

export const useFeatureGate = () => {
  const context = useContext(FeatureGateContext);
  if (!context) {
    throw new Error('useFeatureGate must be used within a FeatureGateProvider');
  }
  return context;
};

interface FeatureGateProviderProps {
  children: ReactNode;
}

const FeatureGateProvider: React.FC<FeatureGateProviderProps> = ({
  children,
}) => {
  const { currentUser, isLoading: authLoading } = useContext(AuthContext);

  // Debug logging
  console.log('FeatureGateProvider render:', {
    currentUser: currentUser
      ? { id: currentUser.id, role: currentUser.role }
      : null,
    authLoading,
  });

  // For non-authenticated users, render immediately without Statsig
  const hasAuthenticatedUser = !!currentUser?.id;

  // If auth is still loading, show loading
  if (authLoading) {
    console.log('Auth still loading');
    return <Loading />;
  }

  // If no authenticated user, render immediately without Statsig
  if (!hasAuthenticatedUser) {
    console.log(
      'No authenticated user, rendering UI immediately without Statsig'
    );
    return (
      <FeatureGateContext.Provider value={{}}>
        {children}
      </FeatureGateContext.Provider>
    );
  }

  console.log(
    'Authenticated user detected, setting up Statsig for:',
    currentUser.id
  );

  // From here on, we have an authenticated user and can set up Statsig
  const statsigUser = {
    userID: currentUser.id,
    custom: {
      role: currentUser.role || 'user',
      company_id: currentUser.company_id || undefined,
      name: currentUser.name || undefined,
      authenticated: true,
    },
  };

  console.log(
    'Created Statsig user object for authenticated user:',
    statsigUser
  );

  return <StatsigWrapper statsigUser={statsigUser}>{children}</StatsigWrapper>;
};

// Separate component to handle Statsig initialization for authenticated users
interface StatsigWrapperProps {
  statsigUser: any;
  children: ReactNode;
}

const StatsigWrapper: React.FC<StatsigWrapperProps> = ({
  statsigUser,
  children,
}) => {
  const { client } = useClientAsyncInit(
    'client-whloHFrX9QWaJD3Zb8Rdi3a2WW542VI4QPFqG6E0M3A',
    statsigUser,
    {
      environment: {
        tier: isLocalHost() ? 'development' : 'production',
      },
    }
  );

  useEffect(() => {
    if (client) {
      console.log(
        'Statsig client initialized for authenticated user:',
        statsigUser.userID
      );
      runStatsigAutoCapture(client);
      runStatsigSessionReplay(client);

      // Log initialization event only for authenticated users
      client.logEvent('statsig_user_initialized', undefined, {
        environment: isLocalHost() ? 'development' : 'production',
        user_type: 'authenticated',
        user_id: statsigUser.userID,
        timestamp: new Date().toISOString(),
        immediate_render: 'true',
      });

      console.log(
        'Logged statsig_user_initialized event for user:',
        statsigUser.userID
      );
    }
  }, [client, statsigUser.userID]);

  return (
    <FeatureGateContext.Provider value={{}}>
      <StatsigProvider client={client} loadingComponent={<Loading />}>
        {children}
      </StatsigProvider>
    </FeatureGateContext.Provider>
  );
};

export default FeatureGateProvider;
